import { animate, keyframes, state, style, transition, trigger } from '@angular/animations';
import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { NotifyService } from '@app/services';
import { BaseForm } from '@forms/base.form';
import { Column } from '@import/classes';
import { EmployeeHumiColumnService } from '@import/services';
import * as XLSX from 'xlsx';

declare type AOA = Array<Array<any>>;

const ANIMATION_TIME = 1500;
const BLUE = 'var(--primary-600)';
const TRANSPARENT = 'transparent';
const BORDER_RADIUS = '5px';

@Component({
    selector: 'app-form-employees-import-upload-file',
    templateUrl: './upload-file.template.html',
    styleUrls: ['./upload-file.style.scss'],
    animations: [
        trigger('pulse', [
            state(
                'true',
                style({
                    border: '1px solid transparent',
                    borderRadius: BORDER_RADIUS,
                })
            ),
            state(
                'false',
                style({
                    border: '1px solid transparent',
                    borderRadius: BORDER_RADIUS,
                })
            ),
            transition(
                'false => true',
                animate(
                    ANIMATION_TIME + 'ms ease-in-out',
                    keyframes([
                        style({ borderColor: BLUE, offset: 0 }),
                        style({ borderColor: TRANSPARENT, offset: 0.25 }),
                        style({ borderColor: BLUE, offset: 0.5 }),
                        style({ borderColor: TRANSPARENT, offset: 0.85 }),
                        style({ borderColor: TRANSPARENT, offset: 1 }),
                    ])
                )
            ),
        ]),
    ],
})
export class UploadFileForm extends BaseForm implements OnInit {
    @Output() onFileUpload: EventEmitter<[Column[], string[][]]> = new EventEmitter<[Column[], string[][]]>();
    headers: string[];
    hasFile: boolean;
    filename: string;
    errors: string[] = [];
    noFile = false;

    constructor(
        private notify: NotifyService,
        private columnService: EmployeeHumiColumnService
    ) {
        super();
    }

    async ngOnInit(): Promise<void> {
        await this.columnService.setDataFieldsColumns();
    }

    onAnimationFinish(): void {
        setTimeout(() => {
            this.noFile = false;
        });
    }

    onSubmit(): void {
        if (!this.form.form.valid) {
            this.noFile = true;
        }
    }

    onFileChange(files: FileList): void {
        const file = files[0]; // Multiple files are not allowed
        const reader: FileReader = new FileReader();
        reader.onload = (e: any) => {
            /* read workbook */
            const bstr: string = e.target.result;
            const wb: XLSX.WorkBook = XLSX.read(bstr, { type: 'binary', raw: true, dateNF: 'yyyy-mm-dd' });

            /* grab first sheet */
            const wsname: string = wb.SheetNames[0];
            const ws: XLSX.WorkSheet = wb.Sheets[wsname];

            /* save data */
            const data = <AOA>XLSX.utils.sheet_to_json(ws, { header: 1, raw: false });
            this.headers = data.shift();
            if (!this.hasValidHeaders()) {
                this.notify.error({
                    key: 'import.importMissingColumnsError',
                    params: { totalColumnCount: this.columnService.getRequiredColumnCount() },
                });
                return;
            }
            setTimeout(() => {
                const sanitizedHeaders = this.headers.map((header: string) => header.replace('(Required)', '').trim());
                /*
                Remove any leading and trailing spaces from any columns with string data type.
                Avoids issues with fields like Compensation Type, Employment Type that have to be matched to Humi's lists.
                */
                const sanitizedData = data.map((row) =>
                    row.map((column) => (typeof column === 'string' ? column.trim() : column))
                );
                this.onFileUpload.emit([Column.fromArray(sanitizedHeaders), sanitizedData]);
                this.hasFile = true;
                this.filename = file.name;
            });
        };
        reader.readAsBinaryString(file);
    }

    cancelUpload(): void {
        this.onFileUpload.emit([[], []]);
        this.hasFile = undefined;
        this.filename = undefined;
        this.errors = [];
    }

    hasValidHeaders(): boolean {
        return this.headers.length >= this.columnService.getRequiredColumnCount();
    }
}
